import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowRight, ArrowLeft, Clock, Target, Lock, CheckCircle,
  Focus, SplitSquareVertical, Brain, MessageSquare, Hand,
  Database, Eye, Zap, Lightbulb, AlertCircle, Trophy, Play, Gift,
  BookOpen, Download, Loader2, Gauge, Layers, Radio, HelpCircle, Image,
  ExternalLink, Users, FileSearch, ShieldAlert, Sparkles, Wrench
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { PageLayout } from '../components/Layout';
import { SEO, PageSEO } from '../components/SEO';
import { apiClient, useAuth } from '../lib/api';
import { toast } from 'sonner';
import { MaintenanceBanner } from '../components/MaintenanceNotice';
import { getEntitlements } from '../lib/entitlements';

const iconMap = {
  Focus: Focus,
  SplitSquareVertical: SplitSquareVertical,
  Brain: Brain,
  MessageSquare: MessageSquare,
  Hand: Hand,
  Database: Database,
  Eye: Eye,
  Zap: Zap,
  Lightbulb: Lightbulb,
  AlertCircle: AlertCircle,
  Gauge: Gauge,
  Layers: Layers,
  BookOpen: BookOpen,
  Radio: Radio,
  FileSearch: FileSearch,
  FileText: FileSearch,
  ShieldAlert: ShieldAlert,
};

// Visual Search Exercise display metadata.
// This keeps the public page aligned with the new live VSE implementation
// even if the existing API/database record still contains legacy cab/signal copy.
const VSE_DISPLAY_META = {
  name: 'Visual Search Exercise (VSE)',
  description: 'Practice sustained visual monitoring, rapid visual comparison and divided attention through a progressive five-level VSE-style exercise.',
  skills_assessed: ['Sustained concentration', 'Visual comparison', 'Divided attention'],
  prepares_for: 'OPC Visual Search Exercise (VSE)',
  total_levels: 5,
  duration_minutes: 7,
  durationLabel: '3–7 min',
  difficulty: 'Progressive',
};

// VSE went live on this date. Show the "NEW TEST" badge/border for 30 days.
const VSE_LAUNCH_DATE = new Date('2026-08-30T00:00:00Z');
const isVSEStillNew = () => {
  const days = (Date.now() - VSE_LAUNCH_DATE.getTime()) / (1000 * 60 * 60 * 24);
  return days >= 0 && days <= 30;
};

const VSE_LEVEL_INFO = {
  1: { name: 'Accuracy', duration: '3 min', comparison: '2.8 sec', access: 'Free credit' },
  2: { name: 'Consistency', duration: '4 min', comparison: '2.6 sec', access: 'Premium' },
  3: { name: 'Concentration', duration: '5 min', comparison: '2.4 sec', access: 'Premium' },
  4: { name: 'Endurance', duration: '6 min', comparison: '2.2 sec', access: 'Premium' },
  5: { name: 'Assessment Preparation', duration: '7 min', comparison: '2.0 sec', access: 'Premium · Full Assessment available' },
};

// Rich SEO content for each test based on RIS-3751-TOM
const testSEOContent = {
  'attention-selective': {
    title: 'Group Bourdon Selective Attention Practice for Train Drivers',
    metaDescription: 'Practise selective attention skills relevant to the Group Bourdon train driver assessment. Build visual scanning, accuracy, consistency and error-control habits.',
    assessmentFamily: 'Group Bourdon',
    officialSkill: 'Selective attention',
    developSkillsFor: 'Group Bourdon / TEA-Occ Selective Attention Test',
    gameScreenshot: '/images/games/concentration-grid.png',
    whatItMeasures: 'This practice develops selective attention: the ability to identify relevant visual information while ignoring similar but incorrect information. It is closely aligned to the Group Bourdon style of task, where accuracy, rhythm and consistency matter more than rushing.',
    whyItMatters: [
      'Train drivers must detect small but safety-critical visual cues reliably.',
      'The task reflects disciplined scanning rather than casual observation.',
      'Strong performance depends on avoiding false positives as well as missed targets.',
      'Consistency across the whole task is more important than a fast start.',
    ],
    realWorldExamples: [
      'Picking out the correct signal or indicator among other lineside information.',
      'Maintaining visual discipline during repetitive route sections.',
      'Avoiding assumptions when several cues look similar.',
      'Continuing calmly after a mistake rather than rushing to recover.',
    ],
    officialStandard: 'RIS-3751-TOM places selective attention under the Attention criterion and links it to the Paper Group Bourdon and TEA-Occ assessment methods.',
    performanceBehaviours: [
      'Methodical left-to-right or line-by-line scanning',
      'Stable pace across the whole exercise',
      'Low false-positive rate',
      'Calm recovery after errors',
    ],
    commonFailureModes: [
      'Rushing to complete more items',
      'Marking items that look approximately right',
      'Losing place under time pressure',
      'Speeding up after an error',
    ],
    tipToImprove: 'Train rhythm and accuracy first. Do not practise as if it is a race. A slightly slower but controlled candidate is usually safer than a fast, erratic one.',
    faqs: [
      { q: 'What is the Group Bourdon test?', a: 'The Group Bourdon is a paper-based concentration test where you scan rows of symbols and mark specific target patterns. It measures your ability to maintain accuracy over time while working at a steady pace.' },
      { q: 'How long is the real selective attention test?', a: 'The actual assessment typically lasts 10-15 minutes of continuous scanning. Our practice mirrors this format to build your stamina and accuracy.' },
      { q: 'Is speed or accuracy more important?', a: 'Accuracy is more important. Assessors look for consistent, methodical work with few errors. A fast but error-prone performance will score lower than steady, accurate work.' },
      { q: 'What score do I need to pass?', a: 'Pass marks vary by TOC but typically require 85%+ accuracy with consistent pacing. Focus on minimising errors rather than maximising speed.' },
    ],
  },

  'attention-divided': {
    title: 'TEA-Occ Divided Attention Practice for Train Drivers',
    metaDescription: 'Practise divided attention skills relevant to TEA-Occ train driver assessment. Build controlled attention switching, dual-task monitoring and workload discipline.',
    assessmentFamily: 'TEA-Occ',
    officialSkill: 'Divided attention',
    developSkillsFor: 'TEA-Occ Divided Attention Test',
    gameScreenshot: '/images/games/dual-task.png',
    whatItMeasures: 'This practice develops divided attention: the ability to switch attention between different sources of information and maintain performance across concurrent demands. It is not about doing everything at once; it is about controlled attention switching.',
    whyItMatters: [
      'Drivers monitor external signals, in-cab displays and auditory cues together.',
      'Workload increases during disruption, degraded working and abnormal situations.',
      'The safest candidates maintain the primary task while responding to secondary information.',
      'Recovery after a missed cue is part of the skill being assessed.',
    ],
    realWorldExamples: [
      'Monitoring lineside information while checking the speedometer.',
      'Listening to an instruction while maintaining situational awareness.',
      'Responding to an alarm without losing control of the main task.',
      'Managing several sources of information during a station approach.',
    ],
    officialStandard: 'RIS-3751-TOM defines divided attention as switching attention between sources of information and performing different tasks in parallel, with TEA-Occ listed under Attention.',
    performanceBehaviours: [
      'Controlled switching between tasks',
      'Balanced attention rather than task fixation',
      'Stable primary-task accuracy',
      'Rapid but calm re-focus after interruptions',
    ],
    commonFailureModes: [
      'Focusing almost entirely on one task',
      'Trying to fully attend to both tasks at once',
      'Panicking after a miss',
      'Changing strategy mid-test',
    ],
    tipToImprove: 'Practise structured switching. Know which task is primary, respond calmly to secondary cues, then return immediately to the main task.',
    faqs: [
      { q: 'What is the TEA-Occ divided attention test?', a: 'The TEA-Occ (Test of Everyday Attention - Occupational) measures your ability to monitor multiple information sources simultaneously. You typically track visual targets while listening for audio cues.' },
      { q: 'Why is divided attention important for train drivers?', a: 'Train drivers must monitor signals, track conditions, in-cab displays, and radio communications simultaneously. This test predicts how well you can manage multiple demands safely.' },
      { q: 'Should I focus on visual or audio tasks?', a: 'Neither should dominate. The key is controlled switching - briefly check secondary tasks then return to the primary task. Fixating on one channel is a common failure mode.' },
      { q: 'How can I improve my dual-task performance?', a: 'Practice structured attention switching. Develop a rhythm: primary task → quick secondary check → back to primary. Stay calm when you miss something and resume your strategy.' },
    ],
  },

  'vigilance': {
    title: 'WAFV Vigilance Practice for Train Drivers',
    metaDescription: 'Practise vigilance and sustained attention skills relevant to the WAFV train driver assessment. Build patience, signal detection and response restraint.',
    assessmentFamily: 'WAFV Vigilance Test',
    officialSkill: 'Vigilance',
    whatItMeasures: 'This practice develops vigilance: the ability to stay alert and respond correctly to infrequent stimuli over an extended period. The skill is quiet alertness, not constant activity.',
    whyItMatters: [
      'Train driving includes long periods of low stimulation.',
      'Rare events must still be detected accurately.',
      'False responses can be as problematic as missed responses.',
      'The assessment rewards sustained control rather than impulsive reactions.',
    ],
    realWorldExamples: [
      'Detecting an unusual trackside condition after routine running.',
      'Remaining alert after repeated normal signal aspects.',
      'Responding only when the correct cue is present.',
      'Maintaining concentration during night or monotonous sections.',
    ],
    officialStandard: 'RIS-3751-TOM lists vigilance as the ability to attend and respond to infrequent stimuli over extended periods, assessed using WAFV.',
    performanceBehaviours: [
      'Low false-alarm rate',
      'Stable reaction times',
      'Patience during low-event periods',
      'No over-compensation after a miss',
    ],
    commonFailureModes: [
      'Pressing “just in case”',
      'Mentally switching off when nothing happens',
      'Fatigue-related drift',
      'Frantic responding after an error',
    ],
    tipToImprove: 'Train calm alertness. Most of the task is doing nothing correctly, then responding cleanly when the right cue appears.',
    developSkillsFor: 'WAFV Vigilance Test',
    gameScreenshot: '/images/games/vigilance-monitor.png',
    faqs: [
      { q: 'What is the WAFV vigilance test?', a: 'The WAFV (Vienna Test System) measures your ability to maintain attention over extended periods with infrequent stimuli. You must respond to rare target signals while avoiding false responses.' },
      { q: 'How long does the vigilance test last?', a: 'The actual assessment typically runs 20-30 minutes of continuous monitoring. Our practice builds the sustained focus required for this duration.' },
      { q: 'What causes most failures in vigilance tests?', a: 'The most common failure modes are false alarms (responding when no target is present) and lapses (missing targets due to attention drift). Both are penalised equally.' },
      { q: 'How can I stay alert during boring periods?', a: 'Develop a passive-active attention state: stay physically relaxed but mentally ready. Avoid trying to force concentration, which causes fatigue. Let targets come to you.' },
    ],
  },

  'memory': {
    title: 'TRP1 Trainability for Rules and Procedures Practice',
    metaDescription: 'Practise memory and trainability skills relevant to TRP1 train driver assessment. Build rule learning, recall and procedural accuracy under time pressure.',
    assessmentFamily: 'TRP1',
    officialSkill: 'Memory and trainability',
    developSkillsFor: 'TRP1 Trainability for Rules and Procedures Test',
    gameScreenshot: '/images/games/memory-rules.png',
    whatItMeasures: 'This practice develops the ability to learn, recall and apply new job-related information within time limits. It reflects the TRP1 style of assessment, where candidates must absorb unfamiliar information and use it accurately.',
    whyItMatters: [
      'Driver training requires large volumes of new rule and procedural knowledge.',
      'Rules must be remembered accurately, not approximately.',
      'Safety-critical instructions often need to be applied under pressure.',
      'Strong candidates show structured learning rather than passive reading.',
    ],
    realWorldExamples: [
      'Learning a new operating instruction during training.',
      'Remembering a briefing accurately later in the shift.',
      'Applying a procedure exactly as instructed.',
      'Recalling who must be contacted during an incident.',
    ],
    officialStandard: 'RIS-3751-TOM links Memory to TRP1 Trainability for Rules and Procedures, covering learning, recall and application of job-related information.',
    performanceBehaviours: [
      'Structured reading',
      'Accurate recall',
      'Minimal rule distortion',
      'Decisive application of learnt information',
    ],
    commonFailureModes: [
      'Reading passively',
      'Paraphrasing rules inaccurately',
      'Relying on confidence instead of recall',
      'Second-guessing under time pressure',
    ],
    tipToImprove: 'Use active recall. Read, cover the material, test yourself, then check exactly where your memory distorted the rule.',
    faqs: [
      { q: 'What is the TRP1 test?', a: 'TRP1 (Trainability for Rules and Procedures) assesses your ability to learn, retain and apply new rule-based information. You study unfamiliar material then answer questions without referring back.' },
      { q: 'How much study time do I get in the real test?', a: 'Study periods vary but are typically 5-10 minutes for each section. You cannot take notes and must rely entirely on memory when answering questions.' },
      { q: 'Should I memorise word-for-word or understand concepts?', a: 'Both matter. Railway rules often hinge on specific wording, so approximate understanding is not enough. Focus on exact phrases, sequences and exceptions.' },
      { q: 'What types of rules are tested?', a: 'The test uses fictional rules to ensure no candidate has prior knowledge. Rules typically cover procedures, sequences, exceptions and conditional actions similar to railway operations.' },
    ],
  },

  'sequencing': {
    title: 'TRP2 Task Sequencing and Priority Practice for Train Drivers',
    metaDescription: 'Practise task sequencing and priority ordering skills for TRP2 train driver assessment. Learn to apply procedural rules to determine correct action sequences.',
    assessmentFamily: 'TRP2 / Trainability',
    officialSkill: 'Rule application and sequencing',
    developSkillsFor: 'TRP2 Trainability for Rules and Procedures Test (Part 2 - Sequencing)',
    gameScreenshot: '/images/games/priority-sorting.png',
    whatItMeasures: 'This practice develops your ability to learn priority rules and apply them to determine the correct sequence of actions. It reflects the cognitive demands of TRP2 assessments where you must order tasks based on given procedural rules.',
    whyItMatters: [
      'Train drivers must prioritise actions correctly in time-critical situations.',
      'Incorrect sequencing can compromise safety even when all actions are correct.',
      'Emergency procedures rely on actions being taken in the right order.',
      'Operational efficiency depends on systematic task management.',
    ],
    realWorldExamples: [
      'Determining the correct order of checks before departing.',
      'Prioritising actions during an emergency brake activation.',
      'Sequencing communication and physical actions during incidents.',
      'Applying priority rules when multiple warnings occur simultaneously.',
    ],
    officialStandard: 'RIS-3751-TOM assesses trainability for learning and applying procedural sequences under TRP2, measuring ability to acquire and use rule-based priority systems.',
    performanceBehaviours: [
      'Accurate rule application',
      'Correct priority ordering',
      'Systematic approach to sequencing',
      'Consistent application under time pressure',
    ],
    commonFailureModes: [
      'Ignoring priority rules in favour of intuition',
      'Confusing the order of similar actions',
      'Rushing without applying the full rule set',
      'Changing sequence mid-task when uncertain',
    ],
    tipToImprove: 'Study the rules carefully before sequencing. Create a mental checklist: which rule applies first? Work through each task asking "what comes before this?" and "what must follow?"',
    faqs: [
      { q: 'What is TRP2?', a: 'TRP2 (Trainability for Rules and Procedures Part 2) assesses your ability to learn priority rules and apply them to sequence tasks correctly. It measures how well you can acquire and use procedural knowledge.' },
      { q: 'How is this different from TRP1?', a: 'TRP1 focuses on memorising and recalling information accurately. TRP2 focuses on applying rules to determine correct sequences - it tests rule application rather than pure recall.' },
      { q: 'Why does task order matter so much?', a: 'In railway operations, doing the right things in the wrong order can be as dangerous as doing the wrong things. Safety procedures are sequenced deliberately based on risk priority.' },
      { q: 'How can I improve my sequencing speed?', a: 'Practice identifying the "anchor" action first - the one that must happen before all others. Then build the sequence outward. With practice, rule application becomes more automatic.' },
    ],
  },

  'rules-acquisition': {
    title: 'RAAT Rules Acquisition Comprehension Practice for Train Drivers',
    metaDescription: 'Practise rules acquisition and comprehension skills for RAAT train driver assessment. Develop reading comprehension and rule interpretation abilities.',
    assessmentFamily: 'RAAT / Rules Acquisition',
    officialSkill: 'Reading comprehension and rule interpretation',
    developSkillsFor: 'Rules Acquisition Aptitude Test (RAAT)',
    gameScreenshot: '/images/games/rule-recall.png',
    whatItMeasures: 'This practice develops your ability to read, comprehend, and correctly interpret written rules and procedures. Unlike memory tests, you can refer back to the passage while answering - testing comprehension rather than recall.',
    whyItMatters: [
      'Train drivers must accurately interpret written procedures and regulations.',
      'Misunderstanding a rule can lead to safety-critical errors.',
      'New procedures and updates require rapid comprehension.',
      'Technical documentation must be interpreted precisely.',
    ],
    realWorldExamples: [
      'Reading and applying a new temporary speed restriction notice.',
      'Interpreting emergency procedure documentation.',
      'Understanding technical fault reporting requirements.',
      'Applying rule book sections to specific scenarios.',
    ],
    officialStandard: 'RIS-3751-TOM recognises RAAT as measuring the aptitude to acquire new rule-based information through reading comprehension and accurate interpretation.',
    performanceBehaviours: [
      'Careful reading',
      'Accurate interpretation',
      'Attention to specific details',
      'Distinguishing between similar rules',
    ],
    commonFailureModes: [
      'Skimming instead of reading carefully',
      'Making assumptions not stated in the text',
      'Confusing similar but different rules',
      'Answering based on general knowledge rather than the passage',
    ],
    tipToImprove: 'Read the passage once for overall understanding, then read questions carefully. Return to the passage to find specific answers - the correct answer is always supported by the text.',
    faqs: [
      { q: 'What is RAAT?', a: 'RAAT (Rules Acquisition Aptitude Test) measures your ability to read, understand, and correctly apply written procedural information. You can refer to the passage while answering questions.' },
      { q: 'Is RAAT open-book?', a: 'Yes, you can refer back to the passage while answering questions. This tests comprehension and interpretation rather than memory. However, you still need to work efficiently within time limits.' },
      { q: 'How is RAAT different from TRP1?', a: 'TRP1 tests memory recall - you study information then answer without reference. RAAT tests comprehension - you can see the passage while answering. RAAT measures understanding, TRP1 measures retention.' },
      { q: 'What reading strategies help with RAAT?', a: 'First, skim the passage for structure. Then read questions before detailed reading. When answering, locate the relevant section and read it carefully. Never assume - always verify against the text.' },
    ],
  },

  'reasoning': {
    title: 'TRP2 Reasoning and Problem-Solving Practice for Train Drivers',
    metaDescription: 'Practise reasoning skills relevant to TRP2 train driver assessment. Build logical decision-making, rule application and fault-diagnosis discipline.',
    assessmentFamily: 'TRP2',
    officialSkill: 'Reasoning',
    developSkillsFor: 'TRP2 Trainability Problem-Solving Test',
    gameScreenshot: '/images/games/reasoning-puzzle.png',
    whatItMeasures: 'This practice develops reasoning: the ability to solve problems, identify relationships, interpret information and make decisions using the information provided. It reflects the discipline needed for TRP2-style tasks.',
    whyItMatters: [
      'Train drivers must interpret information accurately before acting.',
      'Fault diagnosis requires step-by-step reasoning, not guesswork.',
      'Unsafe decisions often come from assumptions rather than lack of effort.',
      'Strong candidates use the available information and avoid overcomplicating it.',
    ],
    realWorldExamples: [
      'Working through an abnormal indication logically.',
      'Identifying which information is relevant to a decision.',
      'Avoiding assumptions during degraded working.',
      'Choosing the safest conclusion from limited information.',
    ],
    officialStandard: 'RIS-3751-TOM links Reasoning to TRP2, covering problem-solving, fault diagnosis and interpretation of instrumentation or information.',
    performanceBehaviours: [
      'Careful reading',
      'Step-by-step logic',
      'Elimination of incorrect options',
      'Decision discipline under time pressure',
    ],
    commonFailureModes: [
      'Overthinking simple rules',
      'Using assumptions from outside the question',
      'Rushing to the first plausible answer',
      'Changing correct answers through doubt',
    ],
    tipToImprove: 'Use only the information given. If the question does not state it, do not invent it. TRP2 rewards disciplined reasoning, not clever assumptions.',
    faqs: [
      { q: 'What is the TRP2 test?', a: 'TRP2 (Trainability for Problem-Solving) assesses your ability to interpret information, identify relationships and reach logical conclusions. Questions require you to apply rules or analyse data systematically.' },
      { q: 'Is TRP2 about speed or accuracy?', a: 'Both matter, but accuracy is paramount. A fast wrong answer scores worse than a slower correct one. Work at a steady pace that allows you to check your reasoning.' },
      { q: 'Can I use my own knowledge in TRP2?', a: 'No. Only use information provided in the question. Adding assumptions from outside the test material is a common cause of failure.' },
      { q: 'What types of reasoning questions appear?', a: 'Expect logical sequences, rule application, data interpretation and fault diagnosis scenarios. All answers must be derivable from the given information.' },
    ],
  },

  'rule-based-prioritising': {
    title: 'Rule Application and Prioritisation Practice for Train Drivers',
    metaDescription: 'Practise rule-based prioritisation, information processing and procedural decision-making for train driver assessment preparation.',
    assessmentFamily: 'TRP-style rule application',
    officialSkill: 'Memory, reasoning and rule compliance',
    developSkillsFor: 'TRP1/TRP2 Rule Application Tests',
    gameScreenshot: '/images/games/priority-sorting.png',
    whatItMeasures: 'This practice develops the ability to interpret rule-based information, prioritise correctly and apply instructions consistently under time pressure. It bridges memory, reasoning and procedural decision-making.',
    whyItMatters: [
      'Railway decisions often involve strict priority rules.',
      'The safest action is not always the fastest or most convenient.',
      'Candidates must apply the rule given, not the rule they expect.',
      'Correct prioritisation prevents unsafe shortcuts.',
    ],
    realWorldExamples: [
      'Deciding which indication or instruction takes priority.',
      'Applying a rule sequence during an abnormal situation.',
      'Checking information in the correct order.',
      'Avoiding shortcuts when under operational pressure.',
    ],
    officialStandard: 'RIS-3751-TOM identifies Memory and Reasoning as core selection criteria, assessed through TRP1 and TRP2, where candidates must learn, recall, interpret and apply information accurately.',
    performanceBehaviours: [
      'Correct priority order',
      'Accurate rule interpretation',
      'No assumption-based shortcuts',
      'Stable performance under time pressure',
    ],
    commonFailureModes: [
      'Following a pattern from a previous question',
      'Ignoring priority rules',
      'Choosing the familiar option',
      'Rushing before checking all information',
    ],
    tipToImprove: 'Slow down at the rule-reading stage. Most errors happen before the answer stage because the candidate has misunderstood the priority rule.',
    faqs: [
      { q: 'What is rule-based prioritisation?', a: 'This skill involves reading multiple rules or instructions, identifying which takes precedence, and applying them in the correct order - essential for railway operations where procedures must be followed precisely.' },
      { q: 'How do I know which rule takes priority?', a: 'Priority is usually stated explicitly in the rules or follows a standard hierarchy (safety > procedures > convenience). Read all given information before deciding.' },
      { q: 'Why do I keep getting priority questions wrong?', a: 'Most errors occur at the reading stage. Candidates often apply rules from previous questions or make assumptions. Treat each question as completely independent.' },
      { q: 'Does this appear in TRP1 or TRP2?', a: 'Elements of prioritisation appear in both. TRP1 focuses on remembering rule priorities, while TRP2 tests applying them to problem scenarios.' },
    ],
  },

  'perception': {
    title: 'ATAVT Perception and Anticipation Practice for Train Drivers',
    metaDescription: 'Practise perception, anticipation and visual judgement skills relevant to ATAVT train driver assessment preparation.',
    assessmentFamily: 'ATAVT',
    officialSkill: 'Perception',
    developSkillsFor: 'ATAVT Adaptive Tachistoscopic Traffic Perception Test',
    gameScreenshot: '/images/games/perception-flash.png',
    whatItMeasures: 'This practice develops perceptual judgement: the ability to anticipate elements in a dynamic environment and make correct decisions based on speed, distance and visual information.',
    whyItMatters: [
      'Train driving depends on accurate speed-distance judgement.',
      'Drivers must anticipate what is likely to happen next.',
      'Late or rushed decisions can lead to poor control inputs.',
      'Consistency of judgement is more important than confidence.',
    ],
    realWorldExamples: [
      'Judging when to begin braking for a station stop.',
      'Assessing the significance of a distant visual cue.',
      'Anticipating a change in train behaviour on approach.',
      'Making measured decisions based on limited visual information.',
    ],
    officialStandard: 'RIS-3751-TOM links Perception to ATAVT, assessing anticipation and correct decision-making in relation to speed and distance.',
    performanceBehaviours: [
      'Measured visual judgement',
      'Consistent timing',
      'Accurate anticipation',
      'Controlled response under uncertainty',
    ],
    commonFailureModes: [
      'Guessing when unsure',
      'Responding too quickly',
      'Over-analysing and missing the decision window',
      'Changing judgement strategy mid-test',
    ],
    tipToImprove: 'Train calibration, not reflexes. Observe first, judge deliberately, then respond when you have enough information.',
    faqs: [
      { q: 'What is the ATAVT test?', a: 'ATAVT (Adaptive Tachistoscopic Traffic Perception Test) measures your ability to quickly perceive and interpret visual scenes. Brief images are shown and you must identify key elements accurately.' },
      { q: 'How fast are the images shown?', a: 'Exposure times vary but can be very brief (fractions of a second). The test adapts to your performance, becoming harder as you improve.' },
      { q: 'What should I focus on in each image?', a: 'Look for hazards, moving elements, and anything requiring a response. Train drivers must rapidly identify safety-critical information in their visual field.' },
      { q: 'Can I improve my perception speed?', a: 'Yes. Practice builds both speed and accuracy. Focus on widening your peripheral awareness and developing efficient scanning patterns.' },
    ],
  },

  'reaction-time': {
    title: 'Reaction Time and Response Control Practice for Train Drivers',
    metaDescription: 'Practise reaction time, response accuracy and inhibition skills relevant to WAFV and ATAVT train driver assessment preparation.',
    assessmentFamily: 'WAFV / ATAVT',
    officialSkill: 'Reaction time',
    developSkillsFor: 'WAFV/ATAVT Reaction Time Tests',
    gameScreenshot: '/images/games/reaction-target.png',
    whatItMeasures: 'This practice develops response speed and response quality. In train driver assessment, reaction time is not just about being fast; it is about responding quickly enough while staying accurate and controlled.',
    whyItMatters: [
      'Drivers must respond to visual and acoustic stimuli appropriately.',
      'An incorrect fast response can be worse than a slightly slower correct response.',
      'Response inhibition is part of safe decision-making.',
      'Consistency matters across the whole assessment, not only the first few items.',
    ],
    realWorldExamples: [
      'Responding correctly to an AWS or warning cue.',
      'Reacting to a changed indication without panic.',
      'Avoiding an unnecessary action when the cue is not present.',
      'Maintaining performance after a missed or late response.',
    ],
    officialStandard: 'RIS-3751-TOM defines reaction time as a quick and adequate response to simple and complex visual and acoustic stimuli, with associated quality of performance, assessed through WAFV and ATAVT.',
    performanceBehaviours: [
      'Fast but controlled responses',
      'Low impulsive-response rate',
      'Stable performance over time',
      'Good recovery after errors',
    ],
    commonFailureModes: [
      'Prioritising speed over accuracy',
      'Responding before confirming the stimulus',
      'Becoming tense and erratic',
      'Trying to compensate after a mistake',
    ],
    tipToImprove: 'Practise clean responses, not frantic ones. The target is reliable speed with accuracy, not raw reflex performance.',
    faqs: [
      { q: 'What reaction time is required for train drivers?', a: 'There is no single target number. Assessors look for consistent, accurate responses within acceptable limits. Reliability matters more than exceptional speed.' },
      { q: 'Will I fail if I make one slow response?', a: 'Unlikely. The test looks at your overall pattern. One outlier rarely causes failure, but persistent slowness or a pattern of errors will be noted.' },
      { q: 'How do I avoid false starts?', a: 'Wait until you are certain the stimulus has appeared. A brief delay is safer than an incorrect response. Build a rhythm: ready, see, respond.' },
      { q: 'Does caffeine or fatigue affect my scores?', a: 'Yes. Both can impact reaction time and consistency. Get adequate sleep before assessment and avoid excessive caffeine which can cause jitteriness.' },
    ],
  },

  'hand-coordination': {
    title: '2HAND Coordination Practice for Train Drivers',
    metaDescription: 'Practise bimanual hand coordination and motor control skills relevant to 2HAND train driver assessment preparation.',
    assessmentFamily: '2HAND',
    officialSkill: 'Hand coordination',
    developSkillsFor: '2HAND Two-Hand Coordination Test',
    gameScreenshot: '/images/games/hand-tracking.png',
    whatItMeasures: 'This practice develops controlled hand coordination: the ability to make accurate, appropriate and smooth movements in response to changing visual information.',
    whyItMatters: [
      'Train handling depends on smooth, proportionate control inputs.',
      'Over-correction under pressure can make performance worse.',
      'Drivers need stable motor control during high workload.',
      'Good coordination is calm, economical and controlled.',
    ],
    realWorldExamples: [
      'Making smooth traction and braking adjustments.',
      'Correcting speed drift without abrupt inputs.',
      'Maintaining control during low-speed movements.',
      'Avoiding tense or exaggerated control movements under pressure.',
    ],
    officialStandard: 'RIS-3751-TOM links Hand coordination to the 2HAND Two Hand Coordination Test, assessing controlled movement in response to complex stimuli.',
    performanceBehaviours: [
      'Smooth correction',
      'Relaxed grip and posture',
      'Small economical movements',
      'Stable performance under load',
    ],
    commonFailureModes: [
      'Over-correcting',
      'Tensing shoulders, arms or hands',
      'Trying to chase perfection',
      'Panicking after small deviations',
    ],
    tipToImprove: 'Focus on smoothness and stability. Small deviations are normal; aggressive correction usually creates bigger errors.',
    faqs: [
      { q: 'What is the 2HAND test?', a: 'The 2HAND test measures your ability to track a moving target using coordinated hand movements. You control two axes simultaneously, requiring smooth, proportionate inputs.' },
      { q: 'Do I need gaming experience to pass?', a: 'No. The test measures coordination, not gaming skill. In fact, gamers sometimes over-correct. Focus on smooth, deliberate movements rather than quick reactions.' },
      { q: 'What if I keep over-correcting?', a: 'This is the most common problem. Relax your grip, breathe steadily, and make smaller movements. Accept small deviations rather than chasing perfect tracking.' },
      { q: 'Is hand coordination trainable?', a: 'Yes. Regular practice builds motor control and reduces tension. Focus on smoothness first, then gradually increase difficulty.' },
    ],
  },

  'communication-accuracy': {
    title: 'MMI Communication Accuracy Practice for Train Drivers',
    metaDescription: 'Practise communication accuracy, listening, verbal comprehension and structured response skills relevant to train driver MMI preparation.',
    assessmentFamily: 'MMI',
    officialSkill: 'Communication',
    developSkillsFor: 'MMI Communication Assessment',
    gameScreenshot: '/images/games/communication-test.png',
    whatItMeasures: 'This practice develops accurate information processing and communication discipline. It supports the skills needed for clear listening, understanding, verbal response and structured explanation in safety-critical contexts.',
    whyItMatters: [
      'Safety-critical communication must be understood exactly.',
      'Drivers must avoid assumptions when receiving instructions.',
      'Clear read-back and confirmation reduce operational risk.',
      'The MMI assesses oral communication and behavioural evidence.',
    ],
    realWorldExamples: [
      'Receiving an instruction and confirming key details.',
      'Explaining an incident clearly and factually.',
      'Avoiding vague or emotional language during pressure.',
      'Communicating uncertainty appropriately rather than guessing.',
    ],
    officialStandard: 'RIS-3751-TOM places Communication under the MMI and defines it as the ability to read, listen, understand, respond appropriately and convey information effectively.',
    performanceBehaviours: [
      'Accurate listening',
      'Clear factual responses',
      'Structured explanation',
      'Confirmation before action',
    ],
    commonFailureModes: [
      'Hearing the general idea but missing key detail',
      'Assuming meaning instead of checking',
      'Rambling under pressure',
      'Failing to clarify uncertainty',
    ],
    tipToImprove: 'Practise repeating back key information accurately and concisely. In safety-critical communication, clarity beats confidence.',
    faqs: [
      { q: 'What is the MMI?', a: 'The MMI (Manager Multiple Interview or similar structured interview) assesses communication skills, behavioural competencies and situational judgement through structured exercises and scenarios.' },
      { q: 'What communication skills are assessed?', a: 'Listening accuracy, clarity of explanation, appropriate questioning, read-back discipline, and the ability to structure information logically under pressure.' },
      { q: 'How should I respond to instructions?', a: 'Listen fully, confirm key details by reading back, ask clarifying questions if uncertain, and avoid assumptions. "I understand you want me to..." is better than guessing.' },
      { q: 'What if I do not understand something?', a: 'Ask for clarification. Railway communication protocols encourage checking rather than guessing. Saying "can you confirm..." shows professional discipline, not weakness.' },
    ],
  },

  'behavioural': {
    title: 'SJE Situational Judgement Practice for Train Drivers',
    metaDescription: 'Prepare for train driver Situational Judgement Exercise practice focused on safety, rule compliance, escalation, communication and professional judgement.',
    assessmentFamily: 'SJE / MMI',
    officialSkill: 'Behavioural aptitudes',
    developSkillsFor: 'Situational Judgement Exercise (SJE)',
    gameScreenshot: '/images/games/situational-judgement.png',
    whatItMeasures: 'This practice develops behavioural judgement: the ability to choose safe, professional and procedurally defensible actions in realistic workplace scenarios. It is aligned to the Situational Judgement Exercise and MMI behavioural aptitude area.',
    whyItMatters: [
      'Train drivers work independently in safety-critical environments.',
      'Operators need predictable, low-risk decision-makers.',
      'The safest response is often procedural rather than heroic.',
      'Behavioural judgement is assessed alongside cognitive and psychomotor ability.',
    ],
    realWorldExamples: [
      'A colleague suggests skipping part of a process to save time.',
      'A passenger reports a possible safety concern before departure.',
      'You are unsure whether information is complete enough to act on.',
      'You realise you may have made an operational mistake.',
    ],
    officialStandard: 'RIS-3751-TOM links behavioural aptitudes to the Situational Judgement Exercise and MMI, focusing on relevant behavioural tendencies for safe train driving.',
    performanceBehaviours: [
      'Safety-first decision-making',
      'Rule and procedure compliance',
      'Appropriate escalation',
      'Professional restraint',
      'Honesty and accountability',
    ],
    commonFailureModes: [
      'Choosing the helpful but unsafe option',
      'Trying to be the lone hero',
      'Prioritising speed or convenience',
      'Failing to escalate when outside authority',
      'Minimising risk through overconfidence',
    ],
    decisionHierarchy: {
      title: 'Railway Decision-Making Hierarchy',
      hierarchy: [
        'Safety of people and railway operations',
        'Rules, procedures and authorised instructions',
        'Communication and escalation',
        'Task completion, convenience or customer preference',
      ],
      conclusion: 'If an answer compromises a higher priority, it is usually wrong even if it appears helpful or efficient.',
    },
    tipToImprove: 'When unsure, choose the option that is safest, most procedural and most defensible. The railway usually rewards controlled judgement over bold initiative.',
    faqs: [
      { q: 'What is a Situational Judgement Exercise?', a: 'The SJE presents realistic workplace scenarios and asks you to rank or select the most appropriate responses. It assesses your professional judgement and alignment with railway safety culture.' },
      { q: 'Is there always one correct answer?', a: 'Usually yes, based on the railway safety hierarchy: safety first, then rules, then communication, then task completion. Options that skip steps or take unnecessary risks are rarely correct.' },
      { q: 'Should I choose what I would actually do?', a: 'Choose what a safe, professional, rule-following train driver should do. Personal preferences or shortcuts that work in other jobs may not align with railway expectations.' },
      { q: 'What if two options seem equally safe?', a: 'Apply the hierarchy: does one follow procedure more closely? Does one involve appropriate escalation? The more defensible, documented option is usually preferred.' },
    ],
  },

  'multitasking-workload': {
    title: 'Workload Management and Multitasking Practice for Train Drivers',
    metaDescription: 'Practise workload management, attention switching and information processing skills relevant to train driver assessment preparation.',
    assessmentFamily: 'TEA-Occ / workload simulation',
    officialSkill: 'Divided attention and workload control',
    developSkillsFor: 'TEA-Occ Workload Management Tests',
    gameScreenshot: '/images/games/multitask-dashboard.png',
    whatItMeasures: 'This practice develops workload management: maintaining accuracy while handling multiple information sources. It builds divided attention, prioritisation and controlled response under cognitive load.',
    whyItMatters: [
      'Train driving workload changes quickly during disruption or abnormal working.',
      'Drivers must prioritise safety-critical information first.',
      'Poor workload management can cause missed cues or rushed decisions.',
      'Strong candidates stay structured when demands increase.',
    ],
    realWorldExamples: [
      'Managing radio communication while monitoring the line ahead.',
      'Checking in-cab information during a complex approach.',
      'Responding to an alert without losing the main driving task.',
      'Separating urgent safety information from background workload.',
    ],
    officialStandard: 'RIS-3751-TOM defines divided attention as switching between information sources and performing tasks in parallel, with TEA-Occ listed as an attention assessment method.',
    performanceBehaviours: [
      'Task prioritisation',
      'Controlled attention switching',
      'Accurate response under pressure',
      'Rapid recovery after interruption',
    ],
    commonFailureModes: [
      'Letting one task dominate',
      'Trying to process everything at once',
      'Rushing under workload',
      'Losing the primary safety task',
    ],
    tipToImprove: 'Always identify the primary safety-critical task. Deal with secondary information without allowing it to take over your attention.',
    faqs: [
      { q: 'How is this different from divided attention?', a: 'Workload management adds the element of varying demand over time. You must adapt your attention strategy as tasks increase, decrease or change priority.' },
      { q: 'What if I cannot keep up with everything?', a: 'Prioritise. Safety-critical tasks must be maintained even if secondary tasks suffer. Assessors look for intelligent prioritisation, not perfect performance on everything.' },
      { q: 'Should I try to multitask simultaneously?', a: 'No. True multitasking is a myth. Use rapid, controlled switching between tasks while maintaining awareness of the primary task.' },
      { q: 'How do train drivers manage real workload?', a: 'Through structured scanning patterns, prioritisation hierarchies, and knowing when to defer non-critical tasks. Build these habits in practice.' },
    ],
  },

  'detail-checking': {
    title: 'Detail Checking & Data Comparison Practice for Train Drivers',
    metaDescription: 'Practise detail checking and error detection skills for train driver assessments. Build accuracy in comparing manifests, consignments and operational data.',
    assessmentFamily: 'Detail Checking',
    officialSkill: 'Data verification and error detection',
    developSkillsFor: 'Detail Checking & Data Comparison Test',
    gameScreenshot: '/images/games/detail-checking.png',
    whatItMeasures: 'This practice develops detail checking skills: the ability to compare source documents against comparison data and accurately identify discrepancies. It reflects the precision required for verifying manifests, consignments and operational documents.',
    whyItMatters: [
      'Train operations require precise verification of documentation before departure.',
      'Errors in manifests, schedules or consignment data can lead to operational failures.',
      'Safety-critical information must be checked accurately under time pressure.',
      'Strong candidates demonstrate systematic comparison and reliable error detection.',
    ],
    realWorldExamples: [
      'Verifying train consist details against the planned formation.',
      'Checking passenger information system data against timetables.',
      'Comparing freight manifest details with consignment documentation.',
      'Validating route and timing information before service commencement.',
    ],
    officialStandard: 'Train operating companies use detail checking assessments to evaluate candidates\' ability to accurately compare and verify operational data - a critical skill for safe train operations.',
    performanceBehaviours: [
      'Systematic field-by-field comparison',
      'Accurate error identification',
      'Efficient use of reference keys and codes',
      'Consistent performance across multiple comparisons',
    ],
    commonFailureModes: [
      'Rushing through comparisons without thorough checking',
      'Missing subtle single-character differences',
      'Confusing similar-looking codes or values',
      'Losing accuracy as the test progresses',
    ],
    tipToImprove: 'Compare field by field, not row by row. Develop a consistent checking pattern and stick to it. Accuracy matters more than speed in the early levels.',
    faqs: [
      { q: 'What is detail checking in train driver assessments?', a: 'Detail checking tests your ability to compare source data (like a manifest) against comparison data and identify any errors or discrepancies. It assesses attention to detail and data verification accuracy.' },
      { q: 'What types of errors should I look for?', a: 'Errors can include transposed digits, wrong letters, incorrect codes, mismatched locations, or altered times. The changes are often subtle - a single character difference.' },
      { q: 'Do I need to memorise codes?', a: 'At higher levels, you will use a reference key to decode consignment codes. You do not need to memorise them - the key is provided. The skill is using the key accurately.' },
      { q: 'How is Level 5 different?', a: 'Level 5 is a full simulation with 50 questions in 10 minutes, including multi-row data and key application. It tests both accuracy and efficiency under time pressure.' },
    ],
  },

  'northern-online-screening': {
    title: 'Northern style Maki test Practice for Train Driver Screening',
    metaDescription: 'Practise the Northern style Maki test with timed perceptual speed, resilience and safety judgement activities for train driver recruitment preparation.',
    keywords: 'Northern style Maki test, Northern style Maki test practice, train driver online screening test, train driver psychometric test UK, perceptual speed test, resilience assessment, safety judgement test',
    assessmentFamily: 'Online screening practice',
    officialSkill: 'Perceptual speed, resilience and safety judgement',
    developSkillsFor: 'Northern style Maki test style online train driver screening assessments',
    gameScreenshot: '/images/games/northern-screening.png',
    whatItMeasures: 'This practice combines rapid visual checking and behavioural judgement under timed conditions. It is designed to reflect comparable task formats and psychological demands commonly seen in Northern style Maki test style online screening assessments for safety-critical roles.',
    whyItMatters: [
      'You must sustain accurate visual discrimination while working at pace.',
      'Behavioural scenarios assess judgement under pressure and after setbacks.',
      'Safety scenarios test procedural discipline, escalation and communication.',
      'The blended format helps you rehearse transitions between different cognitive demands.',
    ],
    realWorldExamples: [
      'Checking similar records quickly without missing subtle differences.',
      'Maintaining composure and accountability after a minor performance setback.',
      'Choosing proportionate safety actions when instructions are unclear.',
      'Balancing pace with accuracy when time pressure increases.',
    ],
    officialStandard: 'This is an independent practice assessment and does not claim to replicate any live employer assessment or scoring model.',
    performanceBehaviours: [
      'Methodical visual comparison under time pressure',
      'Consistent judgement across comparable scenarios',
      'Safety-first decision-making with proportionate escalation',
      'Clear communication and personal accountability',
    ],
    commonFailureModes: [
      'Rushing and sacrificing accuracy in perceptual tasks',
      'Inconsistent behavioural choices across similar contexts',
      'Avoiding escalation when uncertainty is safety-relevant',
      'Overconfident decisions without verification',
    ],
    tipToImprove: 'Practise a repeatable checking method for visual items and use a simple decision routine for behavioural items: stabilise, verify, communicate, then act within procedure.',
    faqs: [
      { q: 'Is this an official Northern or Maki assessment?', a: 'No. It is an independent practice assessment designed to reflect comparable formats and behavioural demands. Recruitment processes and assessment formats may change.' },
      { q: 'How long is the full simulation?', a: 'The simulation is structured into three timed activities: Perceptual Speed (12 minutes), Resilience (8 minutes), and Safety (8 minutes), for approximately 28 minutes in total.' },
      { q: 'Can I do this in practice mode?', a: 'Yes. Practice mode offers an untimed familiarisation with smaller question sets and immediate practice guidance.' },
      { q: 'Does this predict pass or fail?', a: 'No. Results are shown as internal practice indicators only and do not represent employer pass thresholds.' },
    ],
  },
  'visual-search': {
    title: 'Visual Search Exercise (VSE) Practice for Train Driver Selection',
    metaDescription: 'Practice skills relevant to the OPC Visual Search Exercise (VSE): sustained visual monitoring, anomaly detection, rapid visual comparison and divided attention.',
    keywords: 'OPC VSE practice, Visual Search Exercise train driver, train driver VSE, visual search psychometric test, divided attention train driver assessment',
    assessmentFamily: 'OPC Visual Search Exercise (VSE)',
    officialSkill: 'Concentration, visual search, divided attention and situational awareness',
    developSkillsFor: 'OPC Visual Search Exercise (VSE)',
    gameScreenshot: '/images/games/visual-search-preview.jpg',
    whatItMeasures: 'This practice develops sustained visual monitoring, rapid comparison of relevant visual information and divided attention. The exercise is built around three demands: monitoring a repetitive operational pattern for unexpected changes, comparing highlighted visual information accurately, and then performing both tasks together while protecting the primary monitoring task.',
    whyItMatters: [
      'The VSE is designed to place sustained concentration and visual monitoring under repetitive conditions.',
      'Candidates must identify relevant visual changes without responding impulsively to normal events.',
      'Rapid visual comparison adds processing demand while accuracy still matters.',
      'The combined task requires the primary monitoring task to remain protected when workload increases.',
    ],
    realWorldExamples: [
      'Maintaining active monitoring during long periods where most information remains normal.',
      'Recognising an unexpected change rather than relying on what happened on previous cycles.',
      'Comparing relevant visual cues accurately while another task continues in the background.',
      'Prioritising a safety-critical monitoring task when concurrent workload increases.',
    ],
    officialStandard: 'The Visual Search Exercise is an additional proprietary OPC assessment used by some train operators. It is not one of the mandated standardised assessments in the RIS-3751-TOM Issue 4 train driver psychometric battery. This TDP exercise is independent preparation practice and does not reproduce OPC proprietary test content or scoring.',
    performanceBehaviours: [
      'Sustained active monitoring',
      'Low false-alarm and missed-anomaly rates',
      'Accurate visual comparison under time pressure',
      'Protection of the primary monitoring task under divided attention',
    ],
    commonFailureModes: [
      'Mentally switching off during repetitive normal sequences',
      'Pressing the anomaly response when nothing has changed',
      'Sacrificing accuracy by rushing the comparison task',
      'Allowing the secondary comparison task to dominate the primary monitoring task',
    ],
    tipToImprove: 'Prioritise accuracy and active monitoring before speed. Build a repeatable visual-checking rhythm, avoid responding “just in case”, and in the combined task protect operational monitoring first if the two demands compete for attention.',
    faqs: [
      { q: 'What is the Visual Search Exercise (VSE)?', a: 'The VSE is an OPC visual ability assessment used by some train operators. Public OPC information describes it as assessing concentration, multi-tasking, focus on key visual cues and situational awareness. This TDP exercise practises those underlying demands using original content.' },
      { q: 'How do the five TDP VSE levels work?', a: 'Level 1 lasts 3 minutes with a 2.8-second comparison interval. Levels 2 to 5 add one minute each and reduce the comparison interval by 0.2 seconds: 4 minutes at 2.6 seconds, 5 minutes at 2.4 seconds, 6 minutes at 2.2 seconds and 7 minutes at 2.0 seconds. Operational monitoring remains at 2.5 seconds throughout.' },
      { q: 'What is Level 5 Full Assessment mode?', a: 'Level 5 also includes a Full Assessment mode for paid subscribers. It uses 140 operational-monitoring events, 140 visual-comparison events and a combined section with 140 events from each task. It is intended as a high-fidelity practice analogue, not a copy of the official OPC assessment.' },
      { q: 'Does Level 1 require a subscription?', a: 'Level 1 can be accessed by free registered subscribers using a free credit. Levels 2 to 5, including Full Assessment mode, require paid access.' },
      { q: 'Is this the same as the official OPC VSE?', a: 'No. This is independent preparation practice. It uses original visuals, wording, event generation and TDP practice metrics. It does not reproduce OPC proprietary content, scoring or pass thresholds.' },
    ],
  },
};

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
};

export const TestsListPage = () => {
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasGuide, setHasGuide] = useState(false);
  const [guideLoading, setGuideLoading] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    apiClient.getTests()
      .then(res => setTests(
        res.data.map(test => (
          test.slug === 'visual-search'
            ? { ...test, ...VSE_DISPLAY_META }
            : test
        ))
      ))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  // Check if user owns the guide
  useEffect(() => {
    if (user) {
      apiClient.checkGuideOwnership()
        .then(res => setHasGuide(res.data.has_guide))
        .catch(() => setHasGuide(false));
    }
  }, [user]);

  const handleBuyGuide = async () => {
    if (!user) {
      navigate('/register', { state: { from: '/tests' } });
      return;
    }

    setGuideLoading(true);
    try {
      const originUrl = window.location.origin;
      const response = await apiClient.createGuideCheckout(originUrl);
      window.location.href = response.data.url;
    } catch (error) {
      console.error('Guide checkout error:', error);
      console.error('Error response:', error.response?.data);
      if (error.response?.data?.detail === 'You already own this guide') {
        toast.info('You already own this guide!');
        setHasGuide(true);
      } else {
        const errorMsg = error.response?.data?.detail || 'Failed to create checkout session';
        toast.error(errorMsg);
      }
    } finally {
      setGuideLoading(false);
    }
  };

  const handleDownloadGuide = async () => {
    try {
      const response = await apiClient.downloadGuide();
      window.open(response.data.download_url, '_blank');
    } catch (error) {
      toast.error('Failed to download guide');
    }
  };

  return (
    <PageLayout>
      <SEO {...PageSEO.tests} />
      <section className="py-12 bg-[#0B1D51]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Badge className="mb-4 bg-[#FF9F1C] hover:bg-[#FF9F1C]/90 text-white">
              RIS-3751-TOM Aligned
            </Badge>
            <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-white tracking-tight mb-4">
              Practice Assessments
            </h1>
            <p className="text-white/70 max-w-2xl">
              Build the skills needed to prepare you for OPC, TOC or private train driver assessments. 
              Practice exercises aligned with RIS-3751-TOM standards.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-12 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(9)].map((_, i) => (
                <Card key={i} className="animate-pulse">
                  <CardHeader>
                    <div className="w-12 h-12 bg-slate-200 rounded-sm" />
                    <div className="h-6 bg-slate-200 rounded w-3/4 mt-4" />
                  </CardHeader>
                  <CardContent>
                    <div className="h-4 bg-slate-200 rounded w-full mb-2" />
                    <div className="h-4 bg-slate-200 rounded w-2/3" />
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <motion.div 
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
            >
              {tests.map((test) => {
                const IconComponent = iconMap[test.icon] || Target;
                const isVSENew = test.slug === 'visual-search' && isVSEStillNew();
                const isNewTest = isVSENew || (test.is_new && test.slug !== 'detail-checking' && test.slug !== 'visual-search');
                return (
                  <motion.div key={test.id} variants={itemVariants}>
                    <Link to={`/tests/${test.slug}`}>
                      <Card 
                        className={`test-card card-hover h-full bg-white relative ${
                          isNewTest 
                            ? 'border-2 border-[#FF9F1C] shadow-lg shadow-[#FF9F1C]/10 cursor-pointer overflow-visible' 
                            : 'border-slate-200 cursor-pointer'
                        }`}
                        data-testid={`test-card-${test.slug}`}
                      >
                        {/* NEW Badge */}
                        {isNewTest && (
                          <div className="absolute top-2 left-1/2 -translate-x-1/2 z-30">
                            <Badge className="bg-[#FF9F1C] text-white border-0 shadow-md px-3 py-1 whitespace-nowrap font-bold tracking-wide flex items-center gap-1.5 rounded-full">
                              <Sparkles className="w-3.5 h-3.5 vse-twinkle" />
                              {isVSENew ? 'NEW TEST' : 'NEW'}
                            </Badge>
                          </div>
                        )}
                        <CardHeader className="pb-2">
                          <div className="flex items-start justify-between">
                            <div className={`w-12 h-12 rounded-sm flex items-center justify-center ${
                              isNewTest ? 'bg-[#FF9F1C]/10' : 'bg-[#0B1D51]/5'
                            }`}>
                              <IconComponent className={`w-6 h-6 ${isNewTest ? 'text-[#FF9F1C]' : 'text-[#0B1D51]'}`} />
                            </div>
                            <div className="flex gap-2">
                              {test.is_maintenance && (
                                <Badge
                                  className="bg-amber-100 text-amber-800 border border-amber-300 gap-1"
                                  data-testid={`maintenance-badge-${test.slug}`}
                                >
                                  <Wrench className="w-3 h-3" />
                                  Maintenance
                                </Badge>
                              )}
                              <Badge 
                                variant="outline" 
                                className={
                                  test.difficulty === 'Hard' 
                                    ? 'border-red-200 text-red-600' 
                                    : 'border-[#FF9F1C]/30 text-[#FF9F1C]'
                                }
                              >
                                {test.difficulty}
                              </Badge>
                            </div>
                          </div>
                          <CardTitle className="font-heading text-xl font-bold text-[#0B1D51] mt-4">
                            {test.name}
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <p className="text-sm text-[#64748B] mb-3 line-clamp-2">
                            {test.description}
                          </p>
                          
                          {/* Prepares you for badge - prominent placement */}
                          {test.prepares_for && (
                            <div className="mb-3 p-2 bg-[#0B1D51]/5 rounded-sm border-l-2 border-[#0B1D51]">
                              <p className="text-xs text-[#64748B] uppercase tracking-wide mb-0.5">Prepares you for</p>
                              <p className="text-sm font-medium text-[#0B1D51] line-clamp-1">{test.prepares_for}</p>
                            </div>
                          )}
                          
                          <div className="space-y-3">
                            <div className="flex flex-wrap gap-1.5">
                              {test.skills_assessed.slice(0, 3).map((skill, i) => (
                                <span 
                                  key={i}
                                  className="text-xs px-2 py-1 bg-[#F8FAFC] rounded text-[#64748B]"
                                >
                                  {skill}
                                </span>
                              ))}
                            </div>
                            <div className="flex items-center justify-between text-sm pt-2 border-t">
                              <span className="text-[#64748B] flex items-center gap-1">
                                <Clock className="w-4 h-4" />
                                {test.slug === 'visual-search' ? VSE_DISPLAY_META.durationLabel : `${test.duration_minutes} min`}
                              </span>
                              <span className="text-[#64748B]">
                                {test.total_levels} levels
                              </span>
                            </div>
                            <div className="flex items-center gap-2 text-sm">
                              <Lock className="w-4 h-4 text-[#FF9F1C]" />
                              <span className="text-[#FF9F1C] font-medium">
                                {test.slug === 'visual-search'
                                  ? 'Level 1 via free credit · Levels 2–5 Premium'
                                  : 'Premium access required'}
                              </span>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </Link>
                  </motion.div>
                );
              })}
            </motion.div>
          )}

          {/* Interview Preparation Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25 }}
            className="mt-12"
          >
            <Card className="border-2 border-[#0B1D51]/15 bg-gradient-to-br from-white via-[#F8FAFC] to-[#0B1D51]/5 overflow-hidden">
              <div className="grid lg:grid-cols-3 gap-6 p-6">
                <div className="lg:col-span-2 flex flex-col justify-center">
                  <Badge className="w-fit bg-[#0B1D51]/10 text-[#0B1D51] border-[#0B1D51]/20 mb-4">
                    <MessageSquare className="w-3 h-3 mr-1" />
                    Interview Preparation
                  </Badge>

                  <h2 className="font-heading text-2xl sm:text-3xl font-bold text-[#0B1D51] mb-3">
                    Prepare for CBI, MMI and DMI Interviews
                  </h2>

                  <p className="text-[#64748B] mb-5 max-w-3xl">
                    Psychometric tests are only one part of the train driver recruitment process.
                    Many candidates also face structured interviews such as competency-based interviews,
                    the multi-modal interview and the Driver Manager Interview. Build safer, clearer and
                    more evidence-based answers before you sit in front of assessors.
                  </p>

                  <div className="grid sm:grid-cols-3 gap-3 mb-6">
                    <div className="p-4 bg-white rounded-lg border border-slate-200">
                      <div className="flex items-center gap-2 mb-2">
                        <Users className="w-5 h-5 text-[#FF9F1C]" />
                        <h3 className="font-heading font-bold text-[#0B1D51]">
                          CBI
                        </h3>
                      </div>
                      <p className="text-sm text-[#64748B]">
                        Practise competency-based answers using real examples, structured evidence and STAR-style delivery.
                      </p>
                    </div>

                    <div className="p-4 bg-white rounded-lg border border-slate-200">
                      <div className="flex items-center gap-2 mb-2">
                        <MessageSquare className="w-5 h-5 text-[#FF9F1C]" />
                        <h3 className="font-heading font-bold text-[#0B1D51]">
                          MMI
                        </h3>
                      </div>
                      <p className="text-sm text-[#64748B]">
                        Prepare for safety, communication, rule-following, pressure and behavioural evidence questions.
                      </p>
                    </div>

                    <div className="p-4 bg-white rounded-lg border border-slate-200">
                      <div className="flex items-center gap-2 mb-2">
                        <Target className="w-5 h-5 text-[#FF9F1C]" />
                        <h3 className="font-heading font-bold text-[#0B1D51]">
                          DMI
                        </h3>
                      </div>
                      <p className="text-sm text-[#64748B]">
                        Build confidence for Driver Manager Interviews, motivation questions and railway-safety scenarios.
                      </p>
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-2 mb-6">
                    {[
                      'STAR answer structure',
                      'Safety-first examples',
                      'Driver Manager Interview preparation',
                      'MMI behavioural evidence practice',
                    ].map((feature, i) => (
                      <div key={i} className="flex items-center gap-2 text-sm">
                        <CheckCircle className="w-4 h-4 text-[#2CB67D] flex-shrink-0" />
                        <span className="text-[#0B1D51]">{feature}</span>
                      </div>
                    ))}
                  </div>

                  <a
                    href="https://traindriverfoundation.com/interview"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-fit"
                  >
                    <Button className="bg-[#0B1D51] hover:bg-[#0B1D51]/90 text-white">
                      Open Interview Preparation
                      <ExternalLink className="ml-2 w-4 h-4" />
                    </Button>
                  </a>
                </div>

                <div className="flex flex-col justify-center items-center bg-[#0B1D51] rounded-xl p-6 text-center">
                  <div className="w-16 h-16 bg-white/10 rounded-full flex items-center justify-center mb-4">
                    <MessageSquare className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="font-heading text-xl font-bold text-white mb-3">
                    Beyond the Psychometric Tests
                  </h3>
                  <p className="text-white/70 text-sm mb-5">
                    Strong test scores help you progress, but interviews decide whether you can evidence the behaviours,
                    judgement and communication expected of a safe future train driver.
                  </p>
                  <a
                    href="https://traindriverfoundation.com/interview"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full"
                  >
                    <Button variant="secondary" className="w-full">
                      Prepare for Interview
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </Button>
                  </a>
                </div>
              </div>
            </Card>
          </motion.div>

          {/* TDF Psychometric Guide Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="mt-12"
          >
            <Card className="border-2 border-emerald-200 bg-gradient-to-br from-emerald-50/50 to-white overflow-hidden">
              <div className="grid md:grid-cols-2 gap-6 p-6">
                <div className="flex flex-col justify-center">
                  <Badge className="w-fit bg-emerald-500/10 text-emerald-600 border-emerald-200 mb-4">
                    <BookOpen className="w-3 h-3 mr-1" />
                    Official Study Guide
                  </Badge>
                  <h3 className="font-heading text-2xl font-bold text-[#0B1D51] mb-3">
                    TDF Psychometric Guide
                  </h3>
                  <p className="text-[#64748B] mb-4">
                    Comprehensive preparation guide covering all psychometric assessment areas. 
                    Includes expert strategies, practice exercises, and tips from qualified assessors.
                  </p>
                  <div className="grid grid-cols-2 gap-2 mb-6">
                    {[
                      'All assessment areas',
                      'RIS-3751-TOM strategies',
                      'Practice exercises',
                      'Assessor tips',
                    ].map((feature, i) => (
                      <div key={i} className="flex items-center gap-2 text-sm">
                        <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                        <span className="text-[#0B1D51]">{feature}</span>
                      </div>
                    ))}
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="text-3xl font-bold text-emerald-600">£19.99</span>
                    <span className="text-sm text-[#64748B]">one-time purchase</span>
                  </div>
                </div>
                <div className="flex flex-col justify-center items-center bg-emerald-50/50 rounded-lg p-6">
                  <BookOpen className="w-16 h-16 text-emerald-600 mb-4" />
                  <p className="text-sm text-[#64748B] text-center mb-4">
                    Instant PDF download after purchase
                  </p>
                  {hasGuide ? (
                    <Button
                      className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
                      onClick={handleDownloadGuide}
                      data-testid="download-guide-tests-btn"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Download Your Guide
                    </Button>
                  ) : (
                    <Button
                      className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
                      onClick={handleBuyGuide}
                      disabled={guideLoading}
                      data-testid="buy-guide-tests-btn"
                    >
                      {guideLoading ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Processing...
                        </>
                      ) : (
                        <>
                          <BookOpen className="w-4 h-4 mr-2" />
                          Buy Guide - £19.99
                        </>
                      )}
                    </Button>
                  )}
                </div>
              </div>
            </Card>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      {!user?.is_member && (
        <section className="py-16 bg-white border-t">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="font-heading text-2xl sm:text-3xl font-bold text-[#0B1D51] mb-4">
              Start Practicing Today
            </h2>
            <p className="text-[#64748B] mb-6">
              Subscribe to unlock all tests and prepare thoroughly for your train driver assessment.
            </p>
            <Link to="/pricing">
              <Button size="lg" className="bg-[#FF9F1C] hover:bg-[#FF9F1C]/90" data-testid="unlock-all-btn">
                View Pricing Plans
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </section>
      )}
    </PageLayout>
  );
};

export const TestDetailPage = () => {
  const { slug } = useParams();
  const [test, setTest] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [userProgress, setUserProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accessStatus, setAccessStatus] = useState(null);
  const [usingCredit, setUsingCredit] = useState(false);
  const { user, refreshUser } = useAuth();

  useEffect(() => {
    Promise.all([
      apiClient.getTest(slug),
      apiClient.getLeaderboard(slug),
    ])
      .then(([testRes, leaderboardRes]) => {
        const loadedTest = testRes.data;
        setTest(
          slug === 'visual-search'
            ? { ...loadedTest, ...VSE_DISPLAY_META }
            : loadedTest
        );
        setLeaderboard(leaderboardRes.data);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [slug]);

  // Check credit access status
  useEffect(() => {
    if (user) {
      apiClient.checkCreditAccess(slug)
        .then(res => setAccessStatus(res.data))
        .catch(() => setAccessStatus({ is_member: false, free_credits: 0, can_access: false }));
    }
  }, [user, slug]);

  useEffect(() => {
    if (user) {
      apiClient.getProgress()
        .then(res => {
          const progress = res.data.find(p => p.test_slug === slug);
          setUserProgress(progress);
        })
        .catch(console.error);
    }
  }, [user, slug]);

  const handleUseFreeCredit = async () => {
    setUsingCredit(true);
    try {
      await apiClient.useFreeCredit(slug);
      await refreshUser();
      const accessRes = await apiClient.checkCreditAccess(slug);
      setAccessStatus(accessRes.data);
      toast.success('Free credit used! You can now take this test.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to use credit');
    } finally {
      setUsingCredit(false);
    }
  };

  if (loading || !test) {
    return (
      <PageLayout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0B1D51]" />
        </div>
      </PageLayout>
    );
  }

  const IconComponent = iconMap[test.icon] || Target;

  // Unified entitlement (single source of truth) — see lib/entitlements.js
  const { hasUnlimitedAccess, hasActiveSession, hasCredits } = getEntitlements(user, slug, accessStatus);
  // For backward compatibility with the rest of this component
  const isActiveMember = hasUnlimitedAccess;
  const canAccessLevel1 = hasUnlimitedAccess || hasActiveSession;

  // Get SEO content for this test
  const seoContent = testSEOContent[slug];

  return (
    <PageLayout>
      {/* Dynamic SEO for individual test page */}
      <SEO 
        title={seoContent?.title || `${test.name} | Train Driver Psychometrics`}
        description={seoContent?.metaDescription || test.description}
        keywords={seoContent?.keywords || `train driver test, ${test.name}, psychometric assessment, ${test.skills_assessed.join(', ')}, RIS-3751-TOM`}
        canonical={`/tests/${slug}`}
      />
      
      {/* FAQ Schema for Google Rich Results */}
      {seoContent?.faqs && seoContent.faqs.length > 0 && (
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": seoContent.faqs.map(faq => ({
              "@type": "Question",
              "name": faq.q,
              "acceptedAnswer": {
                "@type": "Answer",
                "text": faq.a
              }
            }))
          })}
        </script>
      )}
      
      {/* Product Schema for Test */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Course",
          "name": test.name,
          "description": test.description,
          "provider": {
            "@type": "Organization",
            "name": "Train Driver Psychometrics",
            "url": "https://traindriverpsychometrics.co.uk"
          },
          "courseCode": slug,
          "hasCourseInstance": {
            "@type": "CourseInstance",
            "courseMode": "online",
            "duration": `PT${test.duration_minutes}M`
          }
        })}
      </script>
      
      {/* Header */}
      <section className="py-12 bg-[#0B1D51]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="grid lg:grid-cols-2 gap-8 items-center"
          >
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-14 h-14 bg-white/10 rounded-sm flex items-center justify-center">
                  <IconComponent className="w-7 h-7 text-white" />
                </div>
                <Badge className="bg-[#FF9F1C] hover:bg-[#FF9F1C]/90 text-white">
                  {test.difficulty}
                </Badge>
              </div>
              <h1 className="font-heading text-3xl sm:text-4xl font-bold text-white tracking-tight mb-2">
                {test.name}
              </h1>
              <p className="text-white/70 max-w-2xl mb-4">
                {test.description}
              </p>
              
              {/* Develops Skills For - Key SEO Element */}
              {seoContent?.developSkillsFor && (
                <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 mb-6 border border-white/20">
                  <p className="text-white/60 text-sm uppercase tracking-wide mb-1">This test develops skills needed for</p>
                  <p className="text-white font-semibold text-lg">{seoContent.developSkillsFor}</p>
                </div>
              )}
              
              <div className="flex flex-col sm:flex-row gap-3">
                {test.is_maintenance && !user?.is_admin ? (
                  <div className="w-full max-w-xl">
                    <MaintenanceBanner
                      message={test.maintenance_message}
                      startedAt={test.maintenance_started_at}
                    />
                  </div>
                ) : (
                <>
                {test.is_maintenance && user?.is_admin && (
                  <div className="w-full max-w-xl mb-1">
                    <MaintenanceBanner
                      message={test.maintenance_message}
                      startedAt={test.maintenance_started_at}
                      adminView
                    />
                  </div>
                )}
                {canAccessLevel1 ? (
                  <Link to={`/play/${slug}/1`}>
                    <Button 
                      size="lg" 
                      className="bg-[#FF9F1C] hover:bg-[#FF9F1C]/90 w-full sm:w-auto"
                      data-testid="start-test-btn"
                    >
                      <Play className="w-5 h-5 mr-2" />
                      {slug === 'northern-online-screening' ? 'Start Assessment' : (isActiveMember ? 'Start Test' : 'Start Level 1')}
                    </Button>
                  </Link>
                ) : user && hasCredits ? (
                  <Button 
                    size="lg" 
                    className="bg-[#2CB67D] hover:bg-[#2CB67D]/90 w-full sm:w-auto"
                    onClick={handleUseFreeCredit}
                    disabled={usingCredit}
                    data-testid="use-credit-btn"
                  >
                    <Gift className="w-5 h-5 mr-2" />
                    {usingCredit ? 'Using Credit...' : `Use Free Credit (${user?.free_credits || accessStatus?.free_credits} left)`}
                  </Button>
                ) : user ? (
                  <Link to="/pricing">
                    <Button 
                      size="lg" 
                      className="bg-[#FF9F1C] hover:bg-[#FF9F1C]/90 w-full sm:w-auto"
                      data-testid="upgrade-btn"
                    >
                      <Lock className="w-5 h-5 mr-2" />
                      Subscribe to Start
                    </Button>
                  </Link>
                ) : (
                  <div className="flex flex-col sm:flex-row gap-3">
                    <Link to={`/play/${slug}/1`}>
                      <Button 
                        size="lg" 
                        className="bg-[#2CB67D] hover:bg-[#2CB67D]/90 w-full sm:w-auto"
                        data-testid="try-free-btn"
                      >
                        <Play className="w-5 h-5 mr-2" />
                        Try Free - No Signup
                      </Button>
                    </Link>
                    <Link to="/register" state={{ from: `/tests/${slug}` }}>
                      <Button 
                        size="lg" 
                        variant="outline"
                        className="border-white/30 text-white hover:bg-white/10 w-full sm:w-auto"
                        data-testid="register-free-btn"
                      >
                        <Gift className="w-5 h-5 mr-2" />
                        Create Free Account
                      </Button>
                    </Link>
                  </div>
                )}
                </>
                )}
              </div>
            </div>
            
            {/* Game Screenshot Preview Area */}
            <div className="hidden lg:block">
              <div className="bg-white/5 backdrop-blur-sm rounded-lg border border-white/10 overflow-hidden aspect-video relative">
                {seoContent?.gameScreenshot && (
                  <img 
                    src={seoContent.gameScreenshot} 
                    alt={`${test.name} game preview`}
                    className="w-full h-full object-cover"
                  />
                )}
                {!seoContent?.gameScreenshot && (
                  <div className="absolute inset-0 flex flex-col items-center justify-center text-white/40">
                    <Image className="w-12 h-12 mb-3" />
                    <p className="text-sm">Game Preview</p>
                    <p className="text-xs mt-1">Screenshot coming soon</p>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="py-12 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-6">
              {/* Skills Assessed */}
              <Card className="border-slate-200">
                <CardHeader>
                  <CardTitle className="font-heading text-xl text-[#0B1D51]">
                    Skills Assessed
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    {test.skills_assessed.map((skill, i) => (
                      <div 
                        key={i}
                        className="flex items-center gap-3 p-3 bg-[#F8FAFC] rounded-sm"
                      >
                        <CheckCircle className="w-5 h-5 text-[#2CB67D] flex-shrink-0" />
                        <span className="text-sm font-medium text-[#0F172A]">{skill}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Rich SEO Content Section - What This Test Measures */}
              {testSEOContent[slug] && (
                <Card className="border-slate-200">
                  <CardHeader>
                    <CardTitle className="font-heading text-xl text-[#0B1D51] flex items-center gap-2">
                      <Brain className="w-5 h-5 text-[#0B1D51]" />
                      What This Test Measures
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <p className="text-[#334155] leading-relaxed">
                      {testSEOContent[slug].whatItMeasures}
                    </p>

                    {/* Why It Matters */}
                    <div>
                      <h3 className="font-heading font-semibold text-[#0B1D51] mb-3">
                        Why This Matters for Train Drivers
                      </h3>
                      <ul className="space-y-2">
                        {testSEOContent[slug].whyItMatters.map((item, i) => (
                          <li key={i} className="flex items-start gap-3">
                            <CheckCircle className="w-5 h-5 text-[#2CB67D] flex-shrink-0 mt-0.5" />
                            <span className="text-[#334155]">{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* Real World Examples */}
                    <div className="bg-[#F8FAFC] rounded-lg p-4 border border-slate-100">
                      <h3 className="font-heading font-semibold text-[#0B1D51] mb-3 flex items-center gap-2">
                        <Eye className="w-4 h-4 text-[#FF9F1C]" />
                        Real-World Examples
                      </h3>
                      <ul className="space-y-2">
                        {testSEOContent[slug].realWorldExamples.map((example, i) => (
                          <li key={i} className="flex items-start gap-3 text-sm">
                            <span className="text-[#FF9F1C] font-bold">•</span>
                            <span className="text-[#64748B]">{example}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* Official Standard Reference */}
                    <div className="bg-gradient-to-r from-[#0B1D51]/5 to-transparent rounded-lg p-4 border-l-4 border-[#0B1D51]">
                      <h3 className="font-heading font-semibold text-[#0B1D51] mb-2 flex items-center gap-2">
                        <BookOpen className="w-4 h-4" />
                        {slug === 'visual-search' ? 'Assessment Context' : 'RIS-3751-TOM Standard'}
                      </h3>
                      <p className="text-sm text-[#334155]">
                        {testSEOContent[slug].officialStandard}
                      </p>
                    </div>

                    {/* Tip to Improve */}
                    <div className="bg-[#2CB67D]/10 rounded-lg p-4">
                      <h3 className="font-heading font-semibold text-[#2CB67D] mb-2 flex items-center gap-2">
                        <Lightbulb className="w-4 h-4" />
                        Pro Tip to Improve
                      </h3>
                      <p className="text-sm text-[#334155]">
                        {testSEOContent[slug].tipToImprove}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Frequently Asked Questions - Test Specific */}
              {seoContent?.faqs && seoContent.faqs.length > 0 && (
                <Card className="border-slate-200">
                  <CardHeader>
                    <CardTitle className="font-heading text-xl text-[#0B1D51] flex items-center gap-2">
                      <HelpCircle className="w-5 h-5 text-[#FF9F1C]" />
                      Frequently Asked Questions
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Accordion type="single" collapsible className="w-full space-y-3">
                      {seoContent.faqs.map((faq, index) => (
                        <AccordionItem 
                          key={index} 
                          value={`faq-${index}`} 
                          className="rounded-2xl border border-slate-200 bg-white px-4 shadow-sm data-[state=open]:border-[#0B1D51]/30 data-[state=open]:bg-slate-50"
                          data-testid={`test-faq-${index}`}
                        >
                          <AccordionTrigger className="py-5 text-left hover:no-underline">
                            <span className="pr-4 text-lg sm:text-xl font-bold leading-snug text-[#0F172A]">
                              {faq.q}
                            </span>
                          </AccordionTrigger>
                          <AccordionContent className="pb-6">
                            <p className="text-base sm:text-lg leading-relaxed text-slate-700">
                              {faq.a}
                            </p>
                          </AccordionContent>
                        </AccordionItem>
                      ))}
                    </Accordion>
                  </CardContent>
                </Card>
              )}

              {/* Prepares You For - Official Assessment Link */}
              {test.prepares_for && (
                <Card className="border-slate-200 bg-gradient-to-r from-[#0B1D51]/5 to-transparent">
                  <CardHeader>
                    <CardTitle className="font-heading text-xl text-[#0B1D51] flex items-center gap-2">
                      <Target className="w-5 h-5 text-[#FF9F1C]" />
                      Prepares You For
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-[#334155]">
                      This practice exercise develops the cognitive abilities assessed in official train driver selection, specifically:
                    </p>
                    <div className="mt-3 p-4 bg-white rounded-lg border border-[#0B1D51]/10">
                      <p className="font-medium text-[#0B1D51]">{test.prepares_for}</p>
                    </div>
                    <p className="mt-3 text-sm text-[#64748B]">
                      {slug === 'visual-search' ? (
                        <>
                          The VSE is an additional OPC assessment used by some operators and is not part of the mandatory RIS-3751-TOM Issue 4 standardised battery.
                        </>
                      ) : (
                        <>
                          Based on RIS-3751-TOM requirements. <Link to="/resources/ris-3751-tom-explained" className="text-[#0B1D51] hover:underline">Learn more about official assessments →</Link>
                        </>
                      )}
                    </p>
                  </CardContent>
                </Card>
              )}

              {/* Levels / Assessment Structure */}
              <Card className="border-slate-200">
                <CardHeader>
                  <CardTitle className="font-heading text-xl text-[#0B1D51]">
                    {slug === 'northern-online-screening' ? 'Assessment Structure' : 'Available Levels'}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {slug === 'northern-online-screening' ? (
                    <div className="space-y-3">
                      <div className="rounded-sm border bg-white p-4">
                        <p className="font-medium text-[#0F172A] mb-2">Simulation mode (default)</p>
                        <ul className="text-sm text-[#64748B] space-y-1">
                          <li>Perceptual Speed: 12 minutes</li>
                          <li>Resilience: 8 minutes</li>
                          <li>Safety: 8 minutes</li>
                          <li>No in-test answer checking and no question backtracking</li>
                        </ul>
                      </div>
                      <div className="rounded-sm border bg-white p-4">
                        <p className="font-medium text-[#0F172A] mb-2">Practice mode</p>
                        <p className="text-sm text-[#64748B]">
                          Optional untimed familiarisation with shorter sets and immediate practice guidance.
                        </p>
                      </div>
                    </div>
                  ) : (
                  <div className="space-y-3">
                    {[...Array(test.total_levels)].map((_, i) => {
                      const level = i + 1;
                      // Credit access only unlocks level 1, not all levels
                      // hasUnlimitedAccess = subscription or legacy membership
                      const levelAccessible = hasUnlimitedAccess || (level === 1 && hasActiveSession);
                      const isCompleted = userProgress && userProgress.highest_level >= level;
                      const canUseCredit = level === 1 && !hasUnlimitedAccess && !hasActiveSession && hasCredits;
                      
                      return (
                        <div 
                          key={level}
                          className={`flex items-center justify-between p-4 rounded-sm border ${
                            !levelAccessible && !canUseCredit ? 'bg-slate-50 border-slate-200' : 'bg-white border-slate-200 hover:border-[#0B1D51]'
                          } transition-colors`}
                        >
                          <div className="flex items-center gap-4">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center font-heading font-bold ${
                              isCompleted 
                                ? 'bg-[#2CB67D] text-white' 
                                : canUseCredit
                                  ? 'bg-[#2CB67D]/20 text-[#2CB67D]'
                                  : !levelAccessible
                                    ? 'bg-slate-200 text-slate-400'
                                    : 'bg-[#0B1D51]/10 text-[#0B1D51]'
                            }`}>
                              {isCompleted ? <CheckCircle className="w-5 h-5" /> : level}
                            </div>
                            <div>
                              <p className={`font-medium ${!levelAccessible && !canUseCredit ? 'text-slate-400' : 'text-[#0F172A]'}`}>
                                Level {level}{slug === 'visual-search' ? ` — ${VSE_LEVEL_INFO[level]?.name}` : ''}
                              </p>
                              {slug === 'visual-search' && VSE_LEVEL_INFO[level] && (
                                <p className="text-xs text-[#64748B] mt-0.5">
                                  {VSE_LEVEL_INFO[level].duration} · monitoring 2.5 sec · comparison {VSE_LEVEL_INFO[level].comparison}
                                  {level === 5 ? ' · Full Assessment mode available' : ''}
                                </p>
                              )}
                              <p className="text-sm text-[#64748B]">
                                {hasUnlimitedAccess ? 'Unlocked' : levelAccessible ? 'Free Credit (Level 1 Only)' : canUseCredit ? 'Free Credit Available' : 'Premium'}
                              </p>
                            </div>
                          </div>
                          {levelAccessible ? (
                            <Link to={`/play/${slug}/${level}`}>
                              <Button 
                                variant="outline" 
                                size="sm"
                                data-testid={`play-level-${level}`}
                              >
                                Play
                              </Button>
                            </Link>
                          ) : canUseCredit ? (
                            <Button 
                              variant="outline" 
                              size="sm"
                              className="border-[#2CB67D] text-[#2CB67D] hover:bg-[#2CB67D]/10"
                              onClick={handleUseFreeCredit}
                              disabled={usingCredit}
                              data-testid="use-credit-level-btn"
                            >
                              <Gift className="w-4 h-4 mr-1" />
                              Use Credit
                            </Button>
                          ) : (
                            <div className="flex items-center gap-2 text-[#64748B]">
                              <Lock className="w-4 h-4" />
                              <span className="text-sm">Locked</span>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Test Info */}
              <Card className="border-slate-200">
                <CardHeader>
                  <CardTitle className="font-heading text-xl text-[#0B1D51]">
                    Test Info
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-[#64748B]">Duration</span>
                    <span className="font-mono font-medium text-[#0F172A]">
                      {slug === 'visual-search' ? VSE_DISPLAY_META.durationLabel : `${test.duration_minutes} min`}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-[#64748B]">Total Levels</span>
                    <span className="font-mono font-medium text-[#0F172A]">
                      {test.total_levels}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-[#64748B]">Access</span>
                    {isActiveMember ? (
                      <Badge className="bg-[#2CB67D]/10 text-[#2CB67D]">
                        Full Access
                      </Badge>
                    ) : hasActiveSession ? (
                      <Badge className="bg-[#2CB67D]/10 text-[#2CB67D]">
                        Session Active
                      </Badge>
                    ) : hasCredits ? (
                      <Badge className="bg-[#2CB67D]/10 text-[#2CB67D]">
                        {user?.free_credits || accessStatus?.free_credits} Credit{(user?.free_credits || accessStatus?.free_credits) !== 1 ? 's' : ''}
                      </Badge>
                    ) : (
                      <Badge className="bg-[#FF9F1C]/10 text-[#FF9F1C]">
                        Premium Only
                      </Badge>
                    )}
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-[#64748B]">Difficulty</span>
                    <Badge 
                      variant="outline"
                      className={
                        test.difficulty === 'Hard' 
                          ? 'border-red-200 text-red-600' 
                          : 'border-[#FF9F1C]/30 text-[#FF9F1C]'
                      }
                    >
                      {test.difficulty}
                    </Badge>
                  </div>
                </CardContent>
              </Card>

              {/* Your Progress */}
              {user && userProgress && (
                <Card className="border-slate-200">
                  <CardHeader>
                    <CardTitle className="font-heading text-xl text-[#0B1D51]">
                      Your Progress
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <div className="flex justify-between text-sm mb-2">
                        <span className="text-[#64748B]">Completion</span>
                        <span className="font-mono font-medium">
                          {userProgress.highest_level}/{test.total_levels}
                        </span>
                      </div>
                      <Progress 
                        value={(userProgress.highest_level / test.total_levels) * 100} 
                        className="h-2"
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-[#64748B]">Best Score</span>
                      <span className="font-mono font-medium text-[#0F172A]">
                        {userProgress.best_score}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-[#64748B]">Accuracy</span>
                      <span className="font-mono font-medium text-[#0F172A]">
                        {userProgress.average_accuracy.toFixed(1)}%
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-[#64748B]">Attempts</span>
                      <span className="font-mono font-medium text-[#0F172A]">
                        {userProgress.total_attempts}
                      </span>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Leaderboard */}
              <Card className="border-slate-200">
                <CardHeader>
                  <CardTitle className="font-heading text-xl text-[#0B1D51] flex items-center gap-2">
                    <Trophy className="w-5 h-5 text-[#FF9F1C]" />
                    Leaderboard
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {leaderboard.length === 0 ? (
                    <p className="text-[#64748B] text-sm text-center py-4">
                      No scores yet. Be the first!
                    </p>
                  ) : (
                    <div className="space-y-2">
                      {leaderboard.slice(0, 5).map((entry, i) => {
                        // Convert name to initials (e.g., "John Smith" -> "J.S.")
                        const initials = entry.name
                          ? entry.name.split(' ').map(n => n[0]?.toUpperCase()).filter(Boolean).join('.') + '.'
                          : 'Anon';
                        return (
                          <div 
                            key={i}
                            className="leaderboard-row"
                          >
                            <div className={`leaderboard-rank ${
                              i === 0 ? 'gold' : i === 1 ? 'silver' : i === 2 ? 'bronze' : 'bg-slate-100 text-slate-600'
                            }`}>
                              {i + 1}
                            </div>
                            <div className="flex-1 ml-3">
                              <p className="text-sm font-medium text-[#0F172A]">{initials}</p>
                            </div>
                            <div className="font-mono text-sm font-medium text-[#0B1D51]">
                              {entry.best_score}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Upgrade CTA */}
              {user && !user.is_member && (
                <Card className="border-[#FF9F1C]/30 bg-[#FF9F1C]/5">
                  <CardContent className="pt-6">
                    <div className="text-center">
                      <Lock className="w-8 h-8 text-[#FF9F1C] mx-auto mb-3" />
                      <h3 className="font-heading text-lg font-bold text-[#0B1D51] mb-2">
                        Unlock All Levels
                      </h3>
                      <p className="text-sm text-[#64748B] mb-4">
                        Get unlimited access with premium membership
                      </p>
                      <Link to="/pricing">
                        <Button className="w-full bg-[#FF9F1C] hover:bg-[#FF9F1C]/90">
                          Upgrade Now
                        </Button>
                      </Link>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </div>
      </section>
    </PageLayout>
  );
};